import React from 'react'
import styled from '@emotion/styled'
import { toTitleCase } from '@mobi/utils/string'
import { Currency } from '@mobi/utils/money'
import { colors, font, radius, spacing } from '@mobi/component-library/Foundations'
import { Price } from '@mobi/betslip/Components/Common/Price'
import type { LinkInfo } from '@mobi/betslip/Components/Common/BetCard/types'
import { LinkWrapper } from '../Common/LinkWrapper'

export const SelectionInfo: React.FC<{
  heading: React.ReactNode
  subHeading: React.ReactNode
  iconComponent?: React.ReactNode
  rightAlignedComponent?: React.ReactNode
  linkInfo?: LinkInfo
  priceWin?: string | number | null
  priceWinLastSeen?: number | null
  pricePlace?: string | number | null
  pricePlaceLastSeen?: number | null
  isPriceWinBoosted?: boolean
  isPricePlaceBoosted?: boolean
}> = ({
  heading,
  subHeading,
  priceWin,
  priceWinLastSeen,
  pricePlace,
  pricePlaceLastSeen,
  isPriceWinBoosted,
  isPricePlaceBoosted,
  iconComponent,
  rightAlignedComponent,
  linkInfo,
}) => {
  const priceWinMoney = new Currency(priceWin || 0)
  const pricePlaceMoney = new Currency(pricePlace || 0)

  const hasWinValue = priceWinMoney.value > 0
  const hasPlaceValue = pricePlaceMoney.value > 0

  return (
    <LinkWrapper linkInfo={linkInfo}>
      <WrapperStyled data-testid='BetCard.SelectionInfo'>
        <div>
          {iconComponent && <span>{iconComponent}</span>}
          <h3>
            <span>{typeof heading === 'string' ? toTitleCase(heading) : heading}</span>

            <span>{subHeading}</span>
          </h3>
        </div>

        <div>
          {hasWinValue && (
            <Price
              value={priceWinMoney.value}
              valueLastSeen={priceWinLastSeen}
              shouldDisplayAsBoosted={isPriceWinBoosted}
            />
          )}

          {hasWinValue && hasPlaceValue && <span> / </span>}

          {hasPlaceValue && (
            <Price
              value={pricePlaceMoney.value}
              valueLastSeen={pricePlaceLastSeen}
              isSecondaryDisplay={hasWinValue && hasPlaceValue}
              shouldDisplayAsBoosted={isPricePlaceBoosted}
            />
          )}
        </div>

        {rightAlignedComponent && <div>{rightAlignedComponent}</div>}
      </WrapperStyled>
    </LinkWrapper>
  )
}

// ======
// Styles
// ======

const WrapperStyled = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: spacing.sm,
  padding: spacing.smx1,
  borderRadius: radius.md,
  backgroundColor: colors.surface[200],

  '> div': {
    display: 'flex',
    alignItems: 'center',
  },

  // Selection text info + icon
  '> div:nth-of-type(1)': {
    flex: 1,

    '> span': {
      alignSelf: 'flex-start',
      flexShrink: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '3.6rem',
      height: '3.6rem',
      marginRight: spacing.sm,
      borderRadius: radius.smx1,
      backgroundColor: colors.surface[400],
    },

    '> h3': {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      minWidth: 0,
      padding: 0,
      margin: 0,
      background: 0,
      textTransform: 'none',

      '> span:first-of-type': {
        display: 'flex',
        flexWrap: 'wrap',
        fontSize: font.size.lg.fontSize,
        fontWeight: font.weight.medium,
        letterSpacing: font.size.lg.letterSpacing,
        lineHeight: font.size.lg.lineHeight,
        color: colors.black,
      },

      '> span:last-of-type': {
        fontSize: font.size.md.fontSize,
        fontWeight: font.weight.regular,
        letterSpacing: font.size.md.letterSpacing,
        lineHeight: font.size.md.lineHeight,
        color: colors.neutral[800],
      },
    },
  },

  // Price
  '> div:nth-of-type(2)': {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: spacing.smx2,
    marginLeft: 'auto',

    span: {
      fontSize: font.size.lg.fontSize,
      fontWeight: font.weight.medium,
      letterSpacing: font.size.lg.letterSpacing,
      lineHeight: font.size.lg.lineHeight,
      color: colors.black,
    },
  },

  // Right Aligned Content
  '> div:nth-of-type(3):empty': {
    display: 'none',
  },
})
